import * as React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { mushafTextClass, removeFootnotes, sortLineWords, surahStartingOnLine } from "@/utils";
import { surahsNamesMap } from "@/lib/utils";
import { useColorsSettings, useDialogAyahPreciseViewSettings } from "@/store/settings";
import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from "./ui/popover";
import { DialogDescription, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { WordGlyph } from "./ColoredWord";
import SurahBeginning from "./SurahBeginning";
import Medallion from "./common/Medallion";
import SurahName from "./common/SurahName";

import type { AyahWithWords, Word as FetchedWord } from "@/lib/types";

interface DialogAyahProps {
  ayah: AyahWithWords;
  ayahs: AyahWithWords[];
  onNavigate: (ayahId: number) => void;
  lines: FetchedWord[][];
}

/** [surah, ayah] from "2:255". */
const verseKeyParts = (verseKey: string) => verseKey.split(":").map(Number);

/** A word that shows its translation in a small popover when tapped. */
const TranslatedWord: React.FC<{ word: FetchedWord; showColors: boolean }> = ({
  word,
  showColors,
}) => {
  const bosnian = word.translation_bs?.trim();
  return (
    <Popover>
      <PopoverTrigger
        aria-label={bosnian || word.translation_en}
        className="rounded-[3px] transition-colors duration-150 hover:bg-gold-100/70 active:bg-gold-200/60 data-[state=open]:bg-gold-100/70"
      >
        <WordGlyph word={word} showColors={showColors} />
      </PopoverTrigger>
      <PopoverContent
        side="top"
        sideOffset={6}
        className="w-fit max-w-64 rounded-md bg-emerald-950 px-2 py-1 text-center text-xs font-medium text-paper-50 shadow-float ring-0"
      >
        {bosnian ? (
          <>
            <span lang="bs" className="block">
              {bosnian}
            </span>
            <span lang="en" className="block font-normal text-paper-50/70">
              {word.translation_en}
            </span>
          </>
        ) : (
          <span lang="en">{word.translation_en}</span>
        )}
        <PopoverArrow className="fill-emerald-950" />
      </PopoverContent>
    </Popover>
  );
};

/**
 * Body of the ayah dialog (rendered inside DialogContent): header with the
 * surah and ayah as the dialog title, the ayah (or its lines on the page in
 * the precise view) with word translations, the translations, and prev/next
 * navigation.
 */
const DialogAyah: React.FC<DialogAyahProps> = ({ ayah, ayahs, onNavigate, lines }) => {
  const { dialogAyahPreciseView, toggleDialogAyahPreciseView } =
    useDialogAyahPreciseViewSettings();
  const { colors } = useColorsSettings();
  const bodyRef = React.useRef<HTMLDivElement>(null);

  const [surahNumber, ayahNumber] = verseKeyParts(ayah.verse_key);
  const surahName = surahsNamesMap.get(surahNumber);
  const opening = ayah.page_number <= 2;
  const fontFamily = `p${ayah.page_number}`;
  const englishText = React.useMemo(
    () => removeFootnotes(ayah.translation_en),
    [ayah.translation_en],
  );
  const ayahWords = React.useMemo(() => sortLineWords(ayah.words), [ayah.words]);

  const sortedAyahs = React.useMemo(
    () =>
      [...ayahs].sort((a, b) => {
        const [surahA, ayahA] = verseKeyParts(a.verse_key);
        const [surahB, ayahB] = verseKeyParts(b.verse_key);
        return surahA - surahB || ayahA - ayahB;
      }),
    [ayahs],
  );
  const index = sortedAyahs.findIndex((a) => a.id === ayah.id);
  const nextAyah = index === -1 ? undefined : sortedAyahs[index + 1];
  const prevAyah = index > 0 ? sortedAyahs[index - 1] : undefined;

  // The page's arrow keys are paused while a dialog is open; here they move
  // between ayahs with the same right-to-left mapping (← is the next one).
  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const target =
        event.key === "ArrowLeft" ? nextAyah : event.key === "ArrowRight" ? prevAyah : undefined;
      if (!target) return;
      event.preventDefault();
      onNavigate(target.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nextAyah, prevAyah, onNavigate]);

  // A new ayah starts at the top of the body.
  React.useLayoutEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 });
  }, [ayah.id]);

  // The ayah's lines on the page, plus one line of context either side.
  const relevantLines = React.useMemo(() => {
    const ayahLineNumbers = ayah.words.map((w) => w.line_number);
    const first = Math.min(...ayahLineNumbers) - 1;
    const last = Math.max(...ayahLineNumbers) + 1;

    return lines
      .filter((line) => line[0] && line[0].line_number >= first && line[0].line_number <= last)
      .map((line) => {
        const words = sortLineWords(line);
        return { words, surahStart: surahStartingOnLine(words) };
      });
  }, [ayah.words, lines]);

  return (
    <>
      {/* pr-14 keeps clear of the primitive's close button (top-3 right-3 size-9). */}
      <div className="flex items-center justify-between gap-3 border-b border-paper-300/70 py-3 pl-5 pr-14">
        <DialogTitle className="flex min-w-0 items-center gap-2.5">
          <span aria-hidden className="hidden sm:inline">
            <SurahName number={surahNumber} className="text-[28px] leading-none" />
          </span>
          <span className="truncate text-sm font-semibold text-ink">
            {surahName}
            <span className="sr-only">, ajet {ayahNumber}</span>
          </span>
          <Medallion className="size-8 text-[11px]">{ayahNumber}</Medallion>
        </DialogTitle>
        <label className="flex h-9 shrink-0 cursor-pointer items-center gap-2 rounded-full bg-paper-200/70 pl-3 pr-1.5 text-xs font-medium text-ink-muted">
          <span className="hidden sm:inline">Precizan pregled ajeta</span>
          <span className="sm:hidden">Precizno</span>
          <Switch
            aria-label="Precizan pregled ajeta"
            checked={dialogAyahPreciseView}
            onCheckedChange={toggleDialogAyahPreciseView}
          />
        </label>
      </div>
      <DialogDescription className="sr-only">{ayah.translation_bs}</DialogDescription>

      <div ref={bodyRef} className="mushaf-scope min-h-0 overflow-y-auto px-5 py-6 md:px-8">
        {dialogAyahPreciseView ? (
          <div
            dir="rtl"
            // Shrink-to-fit like the page, so the surah header spans the lines.
            className={`${mushafTextClass(ayah.page_number)} mx-auto w-fit text-ink`}
            style={{ fontFamily }}
          >
            {relevantLines.map(({ words, surahStart }) => (
              <React.Fragment key={words[0].id}>
                {surahStart != null && <SurahBeginning number={surahStart} compact={opening} />}
                <div className="whitespace-nowrap text-center">
                  {words.map((word) =>
                    word.ayah_id === ayah.id ? (
                      <TranslatedWord key={word.id} word={word} showColors={colors} />
                    ) : (
                      <WordGlyph
                        key={word.id}
                        word={word}
                        showColors={false}
                        className="text-ink/35"
                      />
                    ),
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div
            dir="rtl"
            className="flex flex-wrap justify-center text-[32px] leading-[1.9] text-ink md:text-[36px]"
            style={{ fontFamily }}
          >
            {ayahWords.map((word) => (
              <TranslatedWord key={word.id} word={word} showColors={colors} />
            ))}
          </div>
        )}

        <div className="mx-auto mt-5 w-2/3 ornament-rule" />
        <div dir="ltr">
          <p lang="bs" className="mt-5 text-[15px] leading-relaxed text-ink">
            {ayah.translation_bs}
          </p>
          <p lang="en" className="mt-3 text-sm leading-relaxed text-ink-muted">
            {englishText}
          </p>
        </div>
      </div>

      {/* Right-to-left: the next ayah is to the left. */}
      <div className="grid grid-cols-2 gap-2 border-t border-paper-300/70 bg-paper-100/70 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <Button
          variant="outline"
          size="lg"
          className="min-w-0 px-3"
          disabled={!nextAyah}
          onClick={() => nextAyah && onNavigate(nextAyah.id)}
        >
          <ArrowLeft aria-hidden />
          Sljedeći ajet
        </Button>
        <Button
          variant="outline"
          size="lg"
          className="min-w-0 px-3"
          disabled={!prevAyah}
          onClick={() => prevAyah && onNavigate(prevAyah.id)}
        >
          Prethodni ajet
          <ArrowRight aria-hidden />
        </Button>
      </div>
    </>
  );
};

export default React.memo(DialogAyah);
